import React from 'react'

import AIPlanTitlePagePdf from 'features/planner/pages/AIPlanPage/components/AIPlanTitlePagePdf'
import { generatePdfBlob } from 'utils/pdfDownloader'

import { captureFullPageScreenshotWithFallback, FullPageScreenshotResult } from '../utils/fullPageScreenshot'
import { PdfSection, downloadCombinedPdf } from '../utils/pdfCombination'
import { createScreenshotPdfSection } from '../utils/pdfPageInsertion'
import { TabNavigationHelper } from '../utils/tabNavigationHelper'
import {
  AIPlanPdfGenerationContext,
  PdfGenerationResult,
  generateAIPlanMarketAnalysisPdf,
  generateAIPlanPersonasPdf,
  generateAIPlanMediaPlanPdf,
  generateAIPlanProjectionsPdf
} from './aiPlanTabPdfGenerators'

const toLogoData = (logo?: { dataUrl: string; width: number; height: number }) =>
  logo ? { src: logo.dataUrl, width: logo.width, height: logo.height } : undefined

interface TabGenerationConfig {
  tabId: string
  tabName: string
  generator: (context: AIPlanPdfGenerationContext, refs?: any) => Promise<PdfGenerationResult>
  requiresScreenshots: boolean
}

interface TabScreenshotData {
  tabId: string
  tabName: string
  screenshotResult: FullPageScreenshotResult
  pdfSection?: { name: string; blob: Blob }
}

export interface AIPlanMultiTabPdfProgress {
  currentTab: string
  currentStep: number
  totalSteps: number
  isComplete: boolean
  errors: string[]
}

export type AIPlanProgressCallback = (progress: AIPlanMultiTabPdfProgress) => void

const AIPLAN_TAB_CONFIGS = [
  { id: 'executive-summary', name: 'Executive Summary', renderDelay: 500 },
  { id: 'market-analysis', name: 'Analysis & Strategy', renderDelay: 300 },
  { id: 'personas', name: 'Personas', renderDelay: 300 },
  { id: 'media-plan', name: 'Media Plan', renderDelay: 300 },
  { id: 'projections', name: 'Projections', renderDelay: 2000 }
]

export class AIPlanMultiTabPdfService {
  private isStaffPdf = false
  private navigationHelper: TabNavigationHelper
  private progressCallback?: AIPlanProgressCallback

  constructor(isStaffPdf: boolean, progressCallback?: AIPlanProgressCallback) {
    this.isStaffPdf = isStaffPdf
    this.navigationHelper = new TabNavigationHelper()
    this.progressCallback = progressCallback
  }

  private reportProgress(currentTab: string, currentStep: number, totalSteps: number, errors: string[] = []) {
    this.progressCallback?.({
      currentTab,
      currentStep,
      totalSteps,
      isComplete: currentStep >= totalSteps,
      errors
    })
  }

  async generateAllPdfs(
    context: AIPlanPdfGenerationContext,
    onTabChange: (tabId: string) => void,
    tabRefs: {
      ganttChartRef?: React.RefObject<HTMLDivElement>
      marketAnalysisPrintRef?: React.RefObject<HTMLDivElement>
      projectionRefs?: {
        deliveryMetricsRef?: React.RefObject<HTMLDivElement>
        connectedTvRef?: React.RefObject<HTMLDivElement>
        internetDisplayRef?: React.RefObject<HTMLDivElement>
        targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
        billboardLocationsRef?: React.RefObject<HTMLDivElement>
      }
      projectionMapRefs?: {
        targetedHouseholdMapRef?: React.RefObject<any>
        billboardMapRef?: React.RefObject<any>
      }
      mediaplanRef?: React.RefObject<any>
    }
  ): Promise<void> {
    const originalTab = this.navigationHelper.getCurrentTabId()
    const sections: PdfSection[] = []
    const errors: string[] = []
    const screenshotData = new Map<string, TabScreenshotData>()
    const totalSteps = AIPLAN_TAB_CONFIGS.length + 1

    try {
      const tabConfigs: TabGenerationConfig[] = [
        {
          tabId: 'executive-summary',
          tabName: 'Executive Summary',
          generator: _ctx => Promise.resolve({ success: true }),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'market-analysis',
          tabName: 'Analysis & Strategy',
          generator: ctx => generateAIPlanMarketAnalysisPdf(this.isStaffPdf, ctx, tabRefs.marketAnalysisPrintRef),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'personas',
          tabName: 'Personas',
          generator: ctx => generateAIPlanPersonasPdf(this.isStaffPdf, ctx),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'media-plan',
          tabName: 'Media Plan',
          generator: ctx => generateAIPlanMediaPlanPdf(this.isStaffPdf, ctx, tabRefs.ganttChartRef, tabRefs.mediaplanRef),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'projections',
          tabName: 'Projections',
          generator: ctx => generateAIPlanProjectionsPdf(this.isStaffPdf, ctx, tabRefs.projectionRefs, tabRefs.projectionMapRefs),
          requiresScreenshots: false
        }
      ]

      const titlePageBlob = await generatePdfBlob(
        React.createElement(AIPlanTitlePagePdf, {
          organizationName: context.organizationName,
          accountName: context.accountName,
          agencyLogo: toLogoData(context.agencyLogo),
          clientLogo: toLogoData(context.clientLogo),
          isDirect: context.isDirect
        })
      )

      sections.push({
        name: 'Title Page',
        blob: titlePageBlob,
        startOnFrontPage: true
      })

      for (let i = 0; i < tabConfigs.length; i++) {
        const config = tabConfigs[i]

        this.reportProgress(config.tabName, i + 1, totalSteps, errors)

        try {
          await this.navigationHelper.navigateToTab(config.tabId, onTabChange)

          if (config.requiresScreenshots || this.isStaffPdf) {
            const tabConfig = AIPLAN_TAB_CONFIGS.find(tc => tc.id === config.tabId)
            const renderDelay = tabConfig?.renderDelay || 500
            await new Promise(resolve => setTimeout(resolve, renderDelay + 1500))

            if (config.tabId === 'projections') {
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          }

          if (this.isStaffPdf && config.requiresScreenshots) {
            try {
              await new Promise(resolve => setTimeout(resolve, 800))

              const screenshotResult = await captureFullPageScreenshotWithFallback({
                tabId: config.tabId,
                width: 1600,
                height: 2000,
                quality: 100,
                timeout: 20000,
                containerSelector: 'body'
              })

              const tabScreenshot: TabScreenshotData = {
                tabId: config.tabId,
                tabName: config.tabName,
                screenshotResult
              }

              if (screenshotResult.success && screenshotResult.imageData) {
                const pdfSection = await createScreenshotPdfSection(screenshotResult.imageData, config.tabName)
                if (pdfSection) {
                  tabScreenshot.pdfSection = pdfSection
                } else {
                  throw new Error(`Failed to convert screenshot to PDF for ${config.tabName}.`)
                }

                if (screenshotResult.imageData.startsWith('blob:')) {
                  URL.revokeObjectURL(screenshotResult.imageData)
                }
              } else {
                throw new Error(`Failed to capture screenshot for ${config.tabName}: ${screenshotResult.error || 'Unknown error'}`)
              }

              screenshotData.set(config.tabId, tabScreenshot)
            } catch (screenshotError) {
              const tabScreenshot = screenshotData.get(config.tabId)
              if (tabScreenshot?.screenshotResult?.imageData?.startsWith('blob:')) {
                URL.revokeObjectURL(tabScreenshot.screenshotResult.imageData)
              }

              const errorMsg = `Screenshot capture failed for ${config.tabName}: ${screenshotError instanceof Error ? screenshotError.message : String(screenshotError)}`
              throw new Error(errorMsg)
            }
          }

          const timeoutMs = config.tabId === 'projections' ? 180000 : 120000
          const result = await Promise.race([config.generator(context), new Promise<PdfGenerationResult>((_, reject) => setTimeout(() => reject(new Error(`PDF generation timeout after ${timeoutMs}ms`)), timeoutMs))])

          if (result.success) {
            let tocLabelAssignedForTab = false

            if (this.isStaffPdf) {
              const tabScreenshot = screenshotData.get(config.tabId)
              if (tabScreenshot?.pdfSection) {
                sections.push({
                  ...tabScreenshot.pdfSection,
                  startOnFrontPage: true,
                  tocLabel: config.tabName
                })
                tocLabelAssignedForTab = true
              }
            }

            if (result.blob && result.blob.size > 0) {
              sections.push({
                name: config.tabName,
                blob: result.blob,
                startOnFrontPage: true,
                ...(this.isStaffPdf && !tocLabelAssignedForTab && { tocLabel: config.tabName }),
                ...(this.isStaffPdf && config.tabId === 'projections' && { rotateLandscapeToPortrait: true })
              })
            }
          } else {
            const errorMsg = result.error || `Failed to generate ${config.tabName} PDF`
            errors.push(errorMsg)
          }
        } catch (error) {
          const errorMsg = `Failed to generate ${config.tabName}: ${error instanceof Error ? error.message : String(error)}`
          errors.push(errorMsg)

          if (this.isStaffPdf && config.requiresScreenshots && error instanceof Error && error.message.includes('screenshot')) {
            throw error
          }
        }
      }

      if (sections.length === 0) {
        throw new Error('No PDF sections were successfully generated.')
      }

      try {
        const filename = `${context.aiPlan.name} - Complete Campaign Plan.pdf`
        await downloadCombinedPdf(sections, filename, this.isStaffPdf)

        this.reportProgress('Complete', totalSteps, totalSteps, errors)
      } catch (combinationError) {
        const errorMsg = `Failed to combine or download PDF: ${combinationError instanceof Error ? combinationError.message : String(combinationError)}`
        errors.push(errorMsg)
        this.reportProgress('Error', totalSteps, totalSteps, errors)
        throw new Error(errorMsg)
      }
    } catch (error) {
      const errorMsg = `Failed to generate combined PDF: ${error}`
      errors.push(errorMsg)
      this.reportProgress('Error', totalSteps, totalSteps, errors)
      throw new Error(errorMsg)
    } finally {
      if (originalTab) {
        try {
          await this.navigationHelper.navigateToTab(originalTab, onTabChange)
        } catch {
          // Tab restoration is non-fatal
        }
      }
    }
  }
}
